import { useEffect, useRef, useState } from 'react';
import { ArrowPathIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../../core/providers/AuthProvider';
import { TERMS } from '../../../shared/constant/terms';
import { confirmAction, showAlert, showSuccessToast } from '../../../shared/utils/dialogs';
import { clearDraft, draftKey, formatDraftTime, readDraft, writeDraft } from '../../../shared/utils/drafts';
import { getAttendanceForDate, resetAttendance, saveAttendance } from '../services/gradesService';

// Where attendance is read and saved. Class secretaries pass their own (classSecretaryService).
const TEACHER_ATTENDANCE = { getAttendanceForDate, saveAttendance, resetAttendance };

const STATUSES = [
  { value: 'present', label: 'Present', active: 'bg-green-600 border-green-600 text-white' },
  { value: 'absent', label: 'Absent', active: 'bg-red-600 border-red-600 text-white' },
  { value: 'late', label: 'Late', active: 'bg-amber-500 border-amber-500 text-white' },
  { value: 'excused', label: 'Excused', active: 'bg-[#4ea6d0] border-[#4ea6d0] text-white' },
];

// en-CA formats as YYYY-MM-DD in the user's local time, which is what a date input expects.
const todayString = () => new Date().toLocaleDateString('en-CA');

// `initialSection` opens the panel on that section (from a link), if the subject has it.
function AttendancePanel({
  subject,
  sections,
  roster: fullRoster,
  rosterLoading,
  attendanceService = TEACHER_ATTENDANCE,
  initialSection = null,
}) {
  const { user } = useAuth();
  // The term last picked for this subject is kept in the browser, so after a power cut or reload
  // the panel comes back on that term instead of Term 1.
  const termKey = draftKey(user?.id, 'attendance-term', subject.id);
  const [date, setDate] = useState(todayString());
  const [term, setTerm] = useState(() => readDraft(termKey)?.data ?? String(TERMS[0]));
  const termRef = useRef(term);
  termRef.current = term;
  const [section, setSection] = useState(() => (sections.includes(initialSection) ? initialSection : ''));
  // Everything saved on the server for the date, as { studentId: { status, term } }.
  const [records, setRecords] = useState({});
  // The marks shown for the selected term; anything that differs from what's saved is unsaved.
  const [statuses, setStatuses] = useState({});
  // The date the records above were loaded for (they're stale while another date loads).
  const [loadedDate, setLoadedDate] = useState(null);
  // When unsaved marks were restored from the browser backup (after a power cut or reload).
  const [restoredAt, setRestoredAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [resetting, setResetting] = useState(false);

  // Unsaved marks are backed up per date and term, since each term shows its own marks.
  const attendanceDraftKey = (forDate, forTerm) => draftKey(user?.id, 'attendance', subject.id, forDate, forTerm);

  // Only the records saved under the selected term are shown.
  const savedFor = (allRecords, forTerm) =>
    Object.fromEntries(
      Object.entries(allRecords)
        .filter(([, r]) => r.term === forTerm)
        .map(([id, r]) => [id, r.status])
    );
  const savedStatuses = savedFor(records, term);

  // Shows a term's saved marks with any unsaved ones from the backup on top.
  const showTerm = (allRecords, forDate, forTerm) => {
    const draft = readDraft(attendanceDraftKey(forDate, forTerm));
    setStatuses({ ...savedFor(allRecords, forTerm), ...draft?.data.statuses });
    setRestoredAt(draft?.savedAt ?? null);
  };

  useEffect(() => {
    if (!date) return undefined;
    let mounted = true;
    setLoading(true);

    attendanceService.getAttendanceForDate(subject.id, date)
      .then((rows) => {
        if (!mounted) return;
        const loaded = Object.fromEntries(rows.map((r) => [r.student_id, { status: r.status, term: String(r.term) }]));
        setRecords(loaded);
        setLoadedDate(date);
        showTerm(loaded, date, termRef.current);
      })
      .catch((err) => showAlert({ text: err.message || 'Failed to load attendance' }))
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
    // showTerm only reads values already in the dependencies (or the term, through its ref).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject.id, date, attendanceService, user?.id]);

  const changeTerm = (nextTerm) => {
    setTerm(nextTerm);
    if (loadedDate) showTerm(records, loadedDate, nextTerm);
  };

  useEffect(() => {
    writeDraft(termKey, term);
  }, [termKey, term]);

  // Every mark not yet saved is backed up in the browser as it's picked, so a power cut doesn't
  // lose it; once everything is saved the backup goes.
  const unsaved = Object.fromEntries(Object.entries(statuses).filter(([id, status]) => savedStatuses[id] !== status));
  const unsavedJson = JSON.stringify(unsaved);
  useEffect(() => {
    if (!loadedDate) return;
    const key = draftKey(user?.id, 'attendance', subject.id, loadedDate, term);
    if (unsavedJson === '{}') clearDraft(key);
    else writeDraft(key, { statuses: JSON.parse(unsavedJson) });
  }, [user?.id, subject.id, loadedDate, unsavedJson, term]);

  const unsavedCount = Object.keys(unsaved).length;

  const discardUnsaved = () => {
    setStatuses(savedStatuses);
    setRestoredAt(null);
  };

  // A date belongs to one term. These are its records saved under a different term than the one
  // selected; they aren't shown, and saving under the selected term moves them to it.
  const otherTermRecords = Object.entries(records).filter(([, r]) => r.term !== term);
  const otherTerms = [...new Set(otherTermRecords.map(([, r]) => r.term))].sort();

  // Saving and marking act on the students shown, so a section is recorded on its own.
  const roster = section ? fullRoster.filter((s) => s.section === section) : fullRoster;

  // Students without a record for the date start unmarked, not defaulted to present — a status
  // must be picked before that row can be saved.
  const statusOf = (studentId) => statuses[studentId] ?? null;

  const setStatus = (studentId, status) => setStatuses((prev) => ({ ...prev, [studentId]: status }));

  const markAll = (status) => setStatuses(Object.fromEntries(roster.map((s) => [s.id, status])));

  const counts = STATUSES.map((s) => ({
    ...s,
    count: roster.filter((student) => statusOf(student.id) === s.value).length,
  }));
  const unmarkedCount = roster.filter((student) => !statusOf(student.id)).length;
  const recorded = roster.some((s) => savedStatuses[s.id]);

  // Saves `marked` under the selected term. If the date has records under another term, they're
  // moved to this term too (keeping their status) so the date stays in one term — after asking.
  // Returns false if the move wasn't confirmed or the save failed.
  const saveMarked = async (marked) => {
    const markedIds = new Set(marked.map((r) => r.student_id));
    const moved = otherTermRecords
      .filter(([id]) => !markedIds.has(id))
      .map(([id, r]) => ({ student_id: id, status: r.status }));

    if (otherTermRecords.length > 0) {
      const count = otherTermRecords.length;
      const confirmed = await confirmAction({
        title: `Move ${date} to Term ${term}?`,
        text: `Attendance for ${date} is saved under Term ${otherTerms.join(' and ')}. Saving under Term ${term} moves all ${count} saved record${count === 1 ? '' : 's'} of this date to Term ${term}.`,
        confirmText: 'Save and move',
      });
      if (!confirmed) return false;
    }

    const toSave = [...marked, ...moved];
    try {
      await attendanceService.saveAttendance(subject.id, date, Number(term), toSave);
    } catch (err) {
      showAlert({ text: err.message || 'Failed to save attendance' });
      return false;
    }
    setRecords((prev) => ({
      ...prev,
      ...Object.fromEntries(toSave.map((r) => [r.student_id, { status: r.status, term }])),
    }));
    // Moved records now belong to this term, so they're shown with it.
    setStatuses((prev) => ({ ...Object.fromEntries(moved.map((r) => [r.student_id, r.status])), ...prev }));
    return true;
  };

  // Bulk-saves every marked row at once; rows still unmarked are left alone.
  const handleSave = async () => {
    const marked = roster
      .filter((s) => statusOf(s.id))
      .map((s) => ({ student_id: s.id, status: statusOf(s.id) }));
    if (marked.length === 0) return;

    setSaving(true);
    if (await saveMarked(marked)) showSuccessToast('Attendance saved');
    setSaving(false);
  };

  const handleSaveRow = async (student) => {
    const status = statusOf(student.id);
    if (!status) return;

    setSavingId(student.id);
    if (await saveMarked([{ student_id: student.id, status }])) {
      showSuccessToast(`Attendance saved for ${student.firstname}`);
    }
    setSavingId(null);
  };

  // Clears the statuses of the students shown. Any already saved for the date under this term are
  // deleted too, so that needs confirming; unsaved picks are simply cleared.
  const handleReset = async () => {
    const shownIds = roster.map((s) => s.id);
    const savedShownIds = shownIds.filter((id) => savedStatuses[id]);

    if (savedShownIds.length > 0) {
      const count = savedShownIds.length;
      const confirmed = await confirmAction({
        title: 'Reset attendance?',
        text: `This deletes the saved Term ${term} attendance of ${count} student${count === 1 ? '' : 's'} for ${date}${section ? ` in ${section}` : ''}.`,
        confirmText: 'Reset',
        danger: true,
      });
      if (!confirmed) return;

      setResetting(true);
      try {
        await attendanceService.resetAttendance(subject.id, date, savedShownIds);
      } catch (err) {
        showAlert({ text: err.message || 'Failed to reset attendance' });
        return;
      } finally {
        setResetting(false);
      }
      setRecords((prev) => {
        const next = { ...prev };
        savedShownIds.forEach((id) => delete next[id]);
        return next;
      });
    }

    setStatuses((prev) => {
      const next = { ...prev };
      shownIds.forEach((id) => delete next[id]);
      return next;
    });
    if (savedShownIds.length > 0) showSuccessToast('Attendance reset');
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col space-y-3">
      {/* On phones a two-column grid in DOM order: Date | Term, the "already recorded" note,
          Section | Mark All Present, Reset | Save. From sm up it's one row, the sm:order classes
          putting the date and note on the left and Term, Section and the buttons on the right. */}
      <div className="shrink-0 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-2">
        <div className="flex min-w-0 items-center gap-3 sm:order-1">
          <label htmlFor="attendanceDate" className="sr-only sm:not-sr-only text-sm font-medium text-gray-700">Date</label>
          <input
            id="attendanceDate"
            type="date"
            value={date}
            max={todayString()}
            onChange={(e) => setDate(e.target.value)}
            className="w-full min-w-0 sm:w-auto rounded-md border border-[#4ea6d0] px-3 py-2 lg:text-sm focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
          />
        </div>

        <select
          value={term}
          onChange={(e) => changeTerm(e.target.value)}
          aria-label="Term"
          className="min-w-0 sm:order-4 rounded-md border border-[#4ea6d0] px-3 py-2 lg:text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
        >
          {TERMS.map((t) => (
            <option key={t} value={t}>Term {t}</option>
          ))}
        </select>

        {recorded && (
          <span className="col-span-2 sm:order-2 text-xs font-medium text-green-600">
            Already recorded — saving will update it
          </span>
        )}
        {!loading && otherTerms.length > 0 && (
          <span className="col-span-2 sm:order-2 text-xs font-medium text-amber-700">
            {date} is recorded under Term {otherTerms.join(' and ')} — saving here moves it to Term {term}
          </span>
        )}
        {/* Pushes everything after it to the right edge on wider screens. */}
        <span className="hidden sm:block sm:order-3 sm:flex-1" aria-hidden="true" />

        {sections.length > 0 && (
          <select
            value={section}
            onChange={(e) => setSection(e.target.value)}
            aria-label="Filter by section"
            className="min-w-0 sm:order-5 rounded-md border border-[#4ea6d0] px-3 py-2 lg:text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
          >
            <option value="">All Sections</option>
            {sections.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        )}
        <button
          type="button"
          onClick={() => markAll('present')}
          disabled={roster.length === 0}
          className={`sm:order-6 rounded-md border border-[#4ea6d0] px-4 py-2 lg:text-sm font-medium text-[#0b4a71] hover:bg-gray-50 disabled:opacity-50 ${
            sections.length > 0 ? '' : 'col-span-2'
          }`}
        >
          Mark All Present
        </button>

        <button
          type="button"
          onClick={handleReset}
          disabled={resetting || roster.length === 0 || (unmarkedCount === roster.length && !recorded)}
          aria-label={resetting ? 'Resetting attendance' : 'Reset attendance'}
          title="Reset attendance"
          className="sm:order-7 flex items-center justify-center gap-2 rounded-md border border-red-300 px-3 sm:px-4 py-2 lg:text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-5 w-5 ${resetting ? 'animate-spin' : ''}`} />
          <span>{resetting ? 'Resetting...' : 'Reset'}</span>
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !date || roster.length === 0 || unmarkedCount === roster.length}
          aria-label={saving ? 'Saving attendance' : 'Save Attendance'}
          title="Save Attendance"
          className="sm:order-8 flex items-center justify-center gap-2 rounded-md border border-[#4ea6d0] bg-[#0b4a71] px-3 sm:px-4 py-2 lg:text-sm font-medium text-white hover:bg-[#3f6d8e] disabled:opacity-50"
        >
          {/* Phones show the icon with a short "Save"; the full text comes back from the sm breakpoint. */}
          <CheckIcon className={`h-5 w-5 sm:hidden ${saving ? 'animate-pulse' : ''}`} />
          <span className="sm:hidden">{saving ? 'Saving...' : 'Save'}</span>
          <span className="hidden sm:inline">{saving ? 'Saving...' : 'Save Attendance'}</span>
        </button>
      </div>

      {restoredAt && unsavedCount > 0 && !loading && (
        <div className="shrink-0 flex flex-wrap items-center justify-between gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          <span>
            Restored {unsavedCount} unsaved Term {term} mark{unsavedCount === 1 ? '' : 's'} from{' '}
            {formatDraftTime(restoredAt)}. Save to keep them.
          </span>
          <button type="button" onClick={discardUnsaved} className="font-medium underline hover:text-amber-900">
            Discard
          </button>
        </div>
      )}

      <div className="flex-1 min-h-0 bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col">
        {rosterLoading || loading ? (
          <p className="text-center text-gray-500 py-12">Loading…</p>
        ) : roster.length === 0 ? (
          <p className="text-center text-gray-500 py-12">
            {!subject.grade_level
              ? 'Set a grade level for this subject to load its students.'
              : sections.length === 0
                ? 'No sections are assigned to this subject yet. Ask an admin to assign them in Subjects.'
                : 'No students found in the assigned sections.'}
          </p>
        ) : (
          <>
            <div className="shrink-0 flex flex-wrap gap-x-4 gap-y-1 px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
              {counts.map((s) => (
                <span key={s.value}>
                  {s.label}: <span className="font-medium text-gray-700">{s.count}</span>
                </span>
              ))}
              <span>
                Unmarked: <span className="font-medium text-gray-700">{unmarkedCount}</span>
              </span>
            </div>
            <div className="flex-1 min-h-0 overflow-x-auto overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#4ea6d0] text-left text-white sticky top-0 z-10">
                  <tr>
                    <th className="sticky left-0 z-20 bg-[#4ea6d0] px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Section</th>
                    <th className="px-4 py-3 font-medium text-right">Status</th>
                    <th className="px-4 py-3 font-medium text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {roster.map((student) => (
                    <tr key={student.id} className="group hover:bg-[#4ea6d0]/20">
                      <td className="sticky left-0 z-[1] max-w-[9rem] sm:max-w-none bg-white group-hover:bg-[#dcedf6] shadow-[inset_-1px_0_0_#e5e7eb] px-4 py-3 font-medium text-[#0b4a71]">
                        {student.lastname}, {student.firstname} {student.middlename ? `${student.middlename} ` : ''}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{student.section || '—'}</td>
                      <td className="px-4 py-3">
                        {/* Pushed to the right edge; the columns are equal, so every button is as wide as the widest label. */}
                        <div className="ml-auto grid w-max grid-cols-2 gap-1.5 sm:grid-cols-4">
                          {STATUSES.map((s) => (
                            <button
                              key={s.value}
                              type="button"
                              onClick={() => setStatus(student.id, s.value)}
                              aria-pressed={statusOf(student.id) === s.value}
                              className={`rounded-full border px-3 py-1 text-center text-xs font-medium transition ${
                                statusOf(student.id) === s.value
                                  ? s.active
                                  : 'border-gray-300 text-gray-600 hover:border-[#4ea6d0] hover:text-[#0b4a71]'
                              }`}
                            >
                              {s.label}
                            </button>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => handleSaveRow(student)}
                          disabled={!statusOf(student.id) || savingId === student.id}
                          className="rounded-md border border-[#4ea6d0] px-3 py-1.5 text-xs font-medium text-[#0b4a71] hover:bg-[#4ea6d0]/10 disabled:opacity-50"
                        >
                          {savingId === student.id ? 'Saving...' : 'Save'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AttendancePanel;
